import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  CreateProjectDto,
  UpdateProjectDto,
  WorkflowTimelineItem,
  WorkflowStatus,
  WorkflowType,
} from '@cms/models';
import {
  DEFAULT_PROJECT_IMAGE_ASPECT_RATIO,
  DEFAULT_PROJECT_IMAGE_DOWNLOAD_QUALITY,
  DEFAULT_PROJECT_IMAGE_MODEL,
  DEFAULT_PROJECT_OUTPUT_COUNT,
  DEFAULT_PROJECT_VIDEO_ASPECT_RATIO,
  DEFAULT_PROJECT_VIDEO_DOWNLOAD_QUALITY,
  DEFAULT_PROJECT_VIDEO_MODEL,
  PROJECT_IMAGE_ASPECT_RATIO_VALUES,
  PROJECT_IMAGE_DOWNLOAD_QUALITY_VALUES,
  projectImageAspectRatioDisplayLabel,
  projectImageDownloadQualityDisplayLabel,
  PROJECT_IMAGE_MODEL_VALUES,
  projectImageModelDisplayLabel,
  PROJECT_OUTPUT_COUNT_VALUES,
  PROJECT_VIDEO_ASPECT_RATIO_VALUES,
  PROJECT_VIDEO_DOWNLOAD_QUALITY_VALUES,
  PROJECT_VIDEO_MODEL_VALUES,
  projectVideoAspectRatioDisplayLabel,
  projectVideoDownloadQualityDisplayLabel,
  projectVideoModelDisplayLabel,
} from '@cms/project-generation-options';
import { Button } from '@/components/ui/button';
import { CommonModal } from '@/components/common/CommonModal';
import { Toggle } from '@/components/ui/toggle';
import { SearchableMultiSelect } from '@/components/custom/forms/SearchableMultiSelect';
import { cn } from '@/lib/utils';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import { normalizeWorkflowPrompt } from '@cms/utils';
import { extractApiErrorMessage } from '@/lib/api/extract-api-error-message';
import { saasApi } from '@/lib/api/saas.api';
import { withAreaPrefix } from '@/lib/area-routes';
import { useAuthStore } from '@/stores/authStore';
import { projectsApi, useCreateProject, useUpdateProject, useProject } from '@/lib/api/projects';
import {
  projectGenerationSettingsFromApi,
  projectGridRowsToTimelineNodes,
  projectPromptTemplatesFromApi,
  projectTenantIdFromApi,
  timelineNodesFromApiPayload,
  timelineNodesToProjectGridRows,
  parseImportedProjectRowsFromGoogleSheetCsv,
  parseImportedSceneRows,
  type ProjectGridRow,
} from './project-timeline-utils';

function newGridRow(): ProjectGridRow {
  return {
    rowId: crypto.randomUUID(),
    sceneId: 1,
    sceneName: '',
    text2ImagePrompt: '',
    image2VideoPrompt: '',
  };
}

const inputClass =
  'block w-full min-h-[72px] px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-y';
const textInputClass =
  'block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';
const selectClass = (viewOnly: boolean) =>
  cn(textInputClass, 'mt-1', viewOnly && 'cursor-default bg-muted/50');

function mergeSelectOptions<T extends string | number>(
  current: T,
  options: readonly T[],
  labelFor: (value: T) => string,
): { value: T; label: string }[] {
  return [...new Set([current, ...options])].map(value => ({
    value,
    label: labelFor(value),
  }));
}

function outputCountLabel(count: number): string {
  return count === 1 ? '1x' : `${count}x`;
}

/** Reference layout: Text to Image Prompt | Image to Video Prompt (or T2I | I2V) */
const DEFAULT_GOOGLE_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1rcylbzceA5b3oBgz1GMIBz_415AEuszmUjd7BWXUXE4/edit?gid=0#gid=0';

const cancelButtonClass =
  'border-2 border-border bg-muted/40 text-foreground shadow-sm hover:bg-muted hover:border-muted-foreground/35';
const importExcelButtonClass =
  'border border-emerald-500/50 bg-background text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/20';
const importGoogleSheetButtonClass =
  'border border-blue-500/50 bg-background text-blue-600 hover:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20';

type TimelineSaveError =
  | { type: 'i18n'; key: string }
  | { type: 'raw'; message: string }
  | null;

type LocationDraft = {
  name?: string;
  type?: WorkflowType;
  initialNodes?: WorkflowTimelineItem[];
};

export function ProjectTimelinePage() {
  const { act, t, language, col } = useAdminI18n();
  const tl = (key: string, options?: Record<string, string | number>) =>
    t(`pages.projects.timeline.${key}`, options);
  const tlError = (key: string) => tl(`errors.${key}`);

  const { projectId = 'new' } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toAreaPath = (path: string): string => withAreaPrefix(path, location.pathname);
  const viewParam = searchParams.get('view') === '1';

  const hasScope = useAuthStore(s => s.hasScope);
  const hasAnyScope = useAuthStore(s => s.hasAnyScope);
  const tenantId = useAuthStore(s => s.tenantId);
  const canCreate = hasScope('project:create');
  const canUpdate = hasScope('project:update');
  /** Matches backend project global-manage scope semantics. */
  const canManageGlobal = useMemo(
    () =>
      hasAnyScope(['project:manage-global', 'project:*', '*', '*:*']),
    [hasAnyScope],
  );

  const isNew = projectId === 'new';
  const { data: loaded, isLoading, isError, error } = useProject(projectId, !isNew);

  const viewOnly = useMemo(() => {
    if (isNew) return false;
    if (viewParam) return true;
    return !canUpdate;
  }, [isNew, viewParam, canUpdate]);

  const [name, setName] = useState('');
  const type: WorkflowType = 'TENANT';
  const [status, setStatus] = useState<WorkflowStatus>('active');
  const [imagePromptTemplateText, setImagePromptTemplateText] = useState('');
  const [videoPromptTemplateText, setVideoPromptTemplateText] = useState('');
  const [imageModel, setImageModel] = useState<string>(DEFAULT_PROJECT_IMAGE_MODEL);
  const [imageAspectRatio, setImageAspectRatio] = useState<string>(
    DEFAULT_PROJECT_IMAGE_ASPECT_RATIO,
  );
  const [imageOutputCount, setImageOutputCount] = useState<number>(DEFAULT_PROJECT_OUTPUT_COUNT);
  const [imageDownloadQuality, setImageDownloadQuality] = useState<string>(
    DEFAULT_PROJECT_IMAGE_DOWNLOAD_QUALITY,
  );
  const [videoModel, setVideoModel] = useState<string>(DEFAULT_PROJECT_VIDEO_MODEL);
  const [videoAspectRatio, setVideoAspectRatio] = useState<string>(
    DEFAULT_PROJECT_VIDEO_ASPECT_RATIO,
  );
  const [videoAspectRatioManuallySet, setVideoAspectRatioManuallySet] = useState(false);
  const [videoOutputCount, setVideoOutputCount] = useState<number>(DEFAULT_PROJECT_OUTPUT_COUNT);
  const [videoDownloadQuality, setVideoDownloadQuality] = useState<string>(
    DEFAULT_PROJECT_VIDEO_DOWNLOAD_QUALITY,
  );
  const [rows, setRows] = useState<ProjectGridRow[]>([newGridRow()]);
  const [saveError, setSaveError] = useState<TimelineSaveError>(null);
  const [isImportingScenes, setIsImportingScenes] = useState(false);
  const [googleSheetModalOpen, setGoogleSheetModalOpen] = useState(false);
  const [googleSheetUrl, setGoogleSheetUrl] = useState(DEFAULT_GOOGLE_SHEET_URL);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);
  /** Platform account: tenants to create one project row per id (TENANT type, new only). */
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  /** Platform account: tenant for an existing TENANT project (edit). */
  const [platformTenantId, setPlatformTenantId] = useState<string>('');

  const tenantsQuery = useQuery({
    queryKey: ['tenants', 'list'],
    queryFn: () => saasApi.getTenants(),
    enabled: Boolean(canManageGlobal && !tenantId),
  });

  const tenantOptions = useMemo(() => {
    const list = tenantsQuery.data ?? [];
    return list
      .filter((t): t is (typeof list)[number] & { id: string } => typeof t.id === 'string' && t.id.length > 0)
      .map(t => ({
        value: t.id,
        label: `${t.name} (${t.code})`,
      }));
  }, [tenantsQuery.data]);

  useEffect(() => {
    if (!isNew) return;
    const st = location.state as LocationDraft | null;
    if (!st) return;
    if (st.name != null) setName(st.name);
    if (st.initialNodes && st.initialNodes.length > 0) {
      setRows(timelineNodesToProjectGridRows(st.initialNodes));
    }
  }, [isNew, location.state]);

  useEffect(() => {
    if (!loaded || isNew) return;
    setName(loaded.name ?? '');
    const st = (loaded as { status?: WorkflowStatus }).status;
    setStatus(st === 'inactive' ? 'inactive' : 'active');
    const prompts = projectPromptTemplatesFromApi(loaded);
    setImagePromptTemplateText(prompts.image);
    setVideoPromptTemplateText(prompts.video);
    const generation = projectGenerationSettingsFromApi(loaded);
    setImageModel(generation.imageModel);
    setImageAspectRatio(generation.imageAspectRatio);
    setImageOutputCount(generation.imageOutputCount);
    setImageDownloadQuality(generation.imageDownloadQuality);
    setVideoModel(generation.videoModel);
    setVideoAspectRatio(generation.videoAspectRatio);
    setVideoAspectRatioManuallySet(false);
    setVideoOutputCount(generation.videoOutputCount);
    setVideoDownloadQuality(generation.videoDownloadQuality);
    setRows(timelineNodesToProjectGridRows(timelineNodesFromApiPayload(loaded)));
    const resolvedTenantId = projectTenantIdFromApi(loaded) ?? '';
    setPlatformTenantId(resolvedTenantId);
    setSelectedTenantIds(resolvedTenantId ? [resolvedTenantId] : []);
  }, [loaded, isNew]);

  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject();

  const replaceImportedRows = useCallback((importedRows: ProjectGridRow[]) => {
    setRows(importedRows);
  }, []);

  const handleImportScenes = useCallback(
    async (file: File) => {
      setSaveError(null);
      setIsImportingScenes(true);
      try {
        const importedRows = await parseImportedSceneRows(file);
        replaceImportedRows(importedRows);
      } catch (error: unknown) {
        setSaveError({
          type: 'raw',
          message: error instanceof Error ? error.message : tlError('import_failed'),
        });
      } finally {
        setIsImportingScenes(false);
      }
    },
    [replaceImportedRows, tlError],
  );

  const handleImportGoogleSheet = useCallback(async () => {
    setSaveError(null);
    setIsImportingScenes(true);
    try {
      const csv = await projectsApi.fetchGoogleSheetCsv(googleSheetUrl);
      const importedRows = parseImportedProjectRowsFromGoogleSheetCsv(csv);
      replaceImportedRows(importedRows);
      setGoogleSheetModalOpen(false);
    } catch (error: unknown) {
      setSaveError({
        type: 'raw',
        message:
          extractApiErrorMessage(error) ||
          (error instanceof Error ? error.message : tlError('import_sheet_failed')),
      });
    } finally {
      setIsImportingScenes(false);
    }
  }, [googleSheetUrl, replaceImportedRows, tlError]);

  const onSave = async () => {
    if (viewOnly) return;
    setSaveError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setSaveError({ type: 'i18n', key: 'name_required' });
      return;
    }
    const image_prompt_template =
      normalizeWorkflowPrompt(imagePromptTemplateText) || null;
    const video_prompt_template =
      normalizeWorkflowPrompt(videoPromptTemplateText) || null;
    try {
      const timeline_nodes = projectGridRowsToTimelineNodes(rows);
      const generationPayload = {
        image_model: imageModel,
        image_aspect_ratio: imageAspectRatio,
        image_output_count: imageOutputCount,
        image_download_quality: imageDownloadQuality,
        video_model: videoModel,
        video_aspect_ratio: videoAspectRatio,
        video_output_count: videoOutputCount,
        video_download_quality: videoDownloadQuality,
      };
      if (isNew) {
        if (!canCreate) {
          setSaveError({ type: 'i18n', key: 'no_create_permission' });
          return;
        }
        if (!tenantId && canManageGlobal && selectedTenantIds.length === 0) {
          setSaveError({ type: 'i18n', key: 'choose_tenant' });
          return;
        }
        if (!tenantId && canManageGlobal) {
          for (const tid of selectedTenantIds) {
            await createMutation.mutateAsync({
              name: trimmedName,
              type: 'TENANT',
              tenant_id: tid,
              timeline_nodes,
              status,
              image_prompt_template,
              video_prompt_template,
              ...generationPayload,
            } as CreateProjectDto);
          }
        } else {
          await createMutation.mutateAsync({
            name: trimmedName,
            type,
            timeline_nodes,
            status,
            image_prompt_template,
            video_prompt_template,
            ...generationPayload,
          } as CreateProjectDto);
        }
      } else {
        if (!canUpdate) {
          setSaveError({ type: 'i18n', key: 'no_update_permission' });
          return;
        }
        const payload = {
          name: trimmedName,
          type,
          timeline_nodes,
          status,
          image_prompt_template,
          video_prompt_template,
          ...generationPayload,
        } as UpdateProjectDto;

        if (!tenantId && canManageGlobal) {
          const targetTenantIds = selectedTenantIds.filter(Boolean);
          if (targetTenantIds.length === 0) {
            setSaveError({ type: 'i18n', key: 'choose_tenant' });
            return;
          }

          const allTenantProjects = await projectsApi.list({ type: 'TENANT' });
          const originalName = (loaded?.name ?? '').trim();

          for (const tid of targetTenantIds) {
            const existing = allTenantProjects.find(w => {
              const wTenantId = projectTenantIdFromApi(w);
              if (wTenantId !== tid) return false;
              if (w.id === projectId) return true;
              if (!originalName) return false;
              return (w.name ?? '').trim() === originalName;
            });

            if (existing?.id) {
              await updateMutation.mutateAsync({
                id: existing.id,
                payload: {
                  ...payload,
                  tenant_id: tid,
                } as UpdateProjectDto,
              });
            } else {
              await createMutation.mutateAsync({
                name: trimmedName,
                type: 'TENANT',
                tenant_id: tid,
                timeline_nodes,
                status,
                image_prompt_template,
                video_prompt_template,
                ...generationPayload,
              } as CreateProjectDto);
            }
          }
        } else {
          if (!tenantId && canManageGlobal && platformTenantId) {
            payload.tenant_id = platformTenantId;
          }
          await updateMutation.mutateAsync({
            id: projectId,
            payload,
          });
        }
      }
      navigate(toAreaPath('/projects'));
    } catch (e: unknown) {
      setSaveError({ type: 'raw', message: extractApiErrorMessage(e) });
    }
  };

  const saveBlockedReason = useMemo(() => {
    if (viewOnly) return null;
    if (isNew && !canCreate) return tlError('no_create_scope');
    if (!isNew && !canUpdate) return tlError('no_update_scope');
    if (!tenantId && canManageGlobal) {
      if (tenantsQuery.isLoading) {
        return tl('loading_tenants');
      }
      if (tenantsQuery.isError) {
        return tlError('load_tenants_failed');
      }
    }
    return null;
  }, [
    viewOnly,
    isNew,
    canCreate,
    canUpdate,
    tenantId,
    canManageGlobal,
    tenantsQuery.isError,
    tenantsQuery.isLoading,
    tl,
    tlError,
    language,
  ]);

  const sceneCount = useMemo(
    () =>
      rows.filter(
        row =>
          row.text2ImagePrompt.trim().length > 0 && row.image2VideoPrompt.trim().length > 0,
      ).length,
    [rows],
  );

  const title = isNew
    ? tl('title_create')
    : viewOnly
      ? tl('title_view')
      : tl('title_edit');

  const nameRequiredError = saveError?.type === 'i18n' && saveError.key === 'name_required';
  const saveErrorMessage =
    saveError?.type === 'i18n'
      ? tlError(saveError.key)
      : saveError?.type === 'raw'
        ? saveError.message
        : null;
  const saveErrorBelowForm =
    saveError && !nameRequiredError ? saveErrorMessage : null;

  if (!isNew && isLoading) {
    return (
      <div className="w-full max-w-[min(100%,112rem)] mx-auto px-3 py-6 md:px-6">
        <p className="text-sm text-muted-foreground">{tl('loading')}</p>
      </div>
    );
  }

  if (!isNew && isError) {
    return (
      <div className="w-full max-w-[min(100%,112rem)] mx-auto px-3 py-6 md:px-6 space-y-3">
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : tl('load_failed')}
        </p>
        <Link to={toAreaPath('/projects')} className="text-sm text-primary underline">
          {tl('back_to_projects')}
        </Link>
      </div>
    );
  }

  const headerActions = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Link
        to={toAreaPath('/projects')}
        className={cn(
          'inline-flex h-10 items-center justify-center rounded-md px-4 text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          cancelButtonClass,
        )}
      >
        {act('cancel')}
      </Link>
      {viewOnly && canUpdate && !isNew ? (
        <Button
          type="button"
          onClick={() =>
            navigate({ pathname: toAreaPath(`/projects/timeline/${projectId}`), search: '' })
          }
        >
          {act('edit')}
        </Button>
      ) : null}
      {!viewOnly ? (
        <Button
          type="button"
          variant="outline"
          className={importExcelButtonClass}
          onClick={() => importFileInputRef.current?.click()}
          disabled={isImportingScenes}
        >
          {isImportingScenes ? tl('importing') : tl('import_excel')}
        </Button>
      ) : null}
      {!viewOnly ? (
        <Button
          type="button"
          variant="outline"
          className={importGoogleSheetButtonClass}
          onClick={() => {
            setGoogleSheetUrl(prev => (prev.trim() ? prev : DEFAULT_GOOGLE_SHEET_URL));
            setGoogleSheetModalOpen(true);
          }}
          disabled={isImportingScenes}
        >
          {isImportingScenes ? tl('importing') : tl('import_google_sheet')}
        </Button>
      ) : null}
      {!viewOnly ? (
        <Button
          type="button"
          onClick={() => void onSave()}
          disabled={
            Boolean(saveBlockedReason) ||
            createMutation.isPending ||
            updateMutation.isPending ||
            (isNew && !canCreate) ||
            (!isNew && !canUpdate)
          }
        >
          {createMutation.isPending || updateMutation.isPending ? tl('saving') : act('save')}
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-4 w-full max-w-[min(100%,112rem)] mx-auto px-3 py-2 md:px-6 md:py-4">
      <input
        ref={importFileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv,.json,.txt,.md"
        className="hidden"
        onChange={event => {
          const file = event.target.files?.[0];
          if (file) {
            void handleImportScenes(file);
          }
          event.target.value = '';
        }}
      />
      <CommonModal
        open={googleSheetModalOpen}
        onClose={() => {
          if (!isImportingScenes) setGoogleSheetModalOpen(false);
        }}
        title={tl('import_modal_title')}
        titleId="import-google-sheet-title"
        panelClassName="max-w-xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className={cancelButtonClass}
              onClick={() => setGoogleSheetModalOpen(false)}
              disabled={isImportingScenes}
            >
              {act('cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => void handleImportGoogleSheet()}
              disabled={isImportingScenes || !googleSheetUrl.trim()}
            >
              {isImportingScenes ? tl('importing') : tl('import')}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground mb-3">{tl('import_modal_hint')}</p>
        <label htmlFor="google-sheet-url" className="block text-sm font-medium">
          {tl('google_sheet_url')}
        </label>
        <input
          id="google-sheet-url"
          type="url"
          value={googleSheetUrl}
          onChange={e => setGoogleSheetUrl(e.target.value)}
          disabled={isImportingScenes}
          placeholder={DEFAULT_GOOGLE_SHEET_URL}
          className={cn(textInputClass, 'mt-1 w-full')}
        />
      </CommonModal>

      <header className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link
            to={toAreaPath('/projects')}
            className="hover:text-foreground underline-offset-4 hover:underline"
          >
            {tl('breadcrumb_projects')}
          </Link>
          <span aria-hidden>/</span>
          <span>{tl('breadcrumb_timeline')}</span>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 gap-y-2">
          <h1 id="project-timeline-title" className="text-2xl font-bold min-w-0 pr-2">
            {title}
          </h1>
          {headerActions}
        </div>
      </header>

      <div className="glass-card p-4 md:p-6 space-y-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:gap-3 lg:gap-4">
          <div className="min-w-0 w-full flex-1">
            <label htmlFor="wf-timeline-name" className="block text-sm font-medium">
              {tl('name_label')}
            </label>
            <input
              id="wf-timeline-name"
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value);
                if (nameRequiredError) setSaveError(null);
              }}
              disabled={viewOnly}
              aria-invalid={nameRequiredError}
              aria-describedby={nameRequiredError ? 'wf-timeline-name-error' : undefined}
              className={cn(
                textInputClass,
                'mt-1 w-full max-w-full',
                viewOnly && 'cursor-default bg-muted/50',
                nameRequiredError && 'border-destructive focus:border-destructive focus:ring-destructive/25',
              )}
            />
            {nameRequiredError ? (
              <p id="wf-timeline-name-error" className="mt-1.5 text-sm text-destructive" role="alert">
                {tlError('name_required')}
              </p>
            ) : null}
          </div>
          <div className="shrink-0 w-fit max-w-full">
            <Toggle
              id="wf-timeline-status"
              label={tl('status_label')}
              checked={status === 'active'}
              onCheckedChange={on => setStatus(on ? 'active' : 'inactive')}
              disabled={viewOnly}
            />
          </div>
        </div>

        {canManageGlobal && !tenantId ? (
          <div>
            <label htmlFor="wf-timeline-tenants" className="block text-sm font-medium">
              {tl('tenants_label')}
            </label>
            {tenantsQuery.isLoading ? (
              <p id="wf-timeline-tenants" className="mt-1 text-sm text-muted-foreground">
                {tl('loading_tenants')}
              </p>
            ) : !viewOnly ? (
              <SearchableMultiSelect
                id="wf-timeline-tenants"
                aria-label={tl('tenants_aria')}
                className="mt-1"
                options={tenantOptions}
                values={selectedTenantIds}
                onChange={ids => {
                  setSelectedTenantIds(ids);
                  setPlatformTenantId(ids.length ? ids[ids.length - 1]! : '');
                }}
                disabled={viewOnly}
                placeholder={
                  isNew ? tl('tenants_placeholder_new') : tl('tenants_placeholder_edit')
                }
              />
            ) : !isNew && viewOnly ? (
              <p id="wf-timeline-tenants" className="mt-1 text-sm text-muted-foreground">
                {(selectedTenantIds.length > 0 ? selectedTenantIds : platformTenantId ? [platformTenantId] : [])
                  .map(tid => tenantOptions.find(o => o.value === tid)?.label ?? tid)
                  .join(', ')}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-3 rounded-md border border-border p-3">
            <h2 className="text-sm font-semibold">{tl('image_settings')}</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="wf-image-model" className="block text-sm font-medium">
                  {tl('model')}
                </label>
                <select
                  id="wf-image-model"
                  value={imageModel}
                  onChange={e => setImageModel(e.target.value)}
                  disabled={viewOnly}
                  className={selectClass(viewOnly)}
                >
                  {mergeSelectOptions(
                    imageModel,
                    PROJECT_IMAGE_MODEL_VALUES,
                    value => projectImageModelDisplayLabel(value),
                  ).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="wf-image-aspect-ratio" className="block text-sm font-medium">
                  {tl('aspect_ratio')}
                </label>
                <select
                  id="wf-image-aspect-ratio"
                  value={imageAspectRatio}
                  onChange={e => {
                    const nextAspectRatio = e.target.value;
                    setImageAspectRatio(nextAspectRatio);
                    if (
                      !videoAspectRatioManuallySet &&
                      (nextAspectRatio === 'portrait' || nextAspectRatio === 'landscape')
                    ) {
                      setVideoAspectRatio(nextAspectRatio);
                    }
                  }}
                  disabled={viewOnly}
                  className={selectClass(viewOnly)}
                >
                  {mergeSelectOptions(
                    imageAspectRatio,
                    PROJECT_IMAGE_ASPECT_RATIO_VALUES,
                    value => projectImageAspectRatioDisplayLabel(value),
                  ).map(
                    option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ),
                  )}
                </select>
              </div>
              <div>
                <label htmlFor="wf-image-output-count" className="block text-sm font-medium">
                  {tl('output_count')}
                </label>
                <select
                  id="wf-image-output-count"
                  value={imageOutputCount}
                  onChange={e => setImageOutputCount(Number(e.target.value))}
                  disabled={viewOnly}
                  className={selectClass(viewOnly)}
                >
                  {mergeSelectOptions(
                    imageOutputCount,
                    PROJECT_OUTPUT_COUNT_VALUES,
                    outputCountLabel,
                  ).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="wf-image-download-quality" className="block text-sm font-medium">
                  {tl('download_quality')}
                </label>
                <select
                  id="wf-image-download-quality"
                  value={imageDownloadQuality}
                  onChange={e => setImageDownloadQuality(e.target.value)}
                  disabled={viewOnly}
                  className={selectClass(viewOnly)}
                >
                  {mergeSelectOptions(
                    imageDownloadQuality,
                    PROJECT_IMAGE_DOWNLOAD_QUALITY_VALUES,
                    value => projectImageDownloadQualityDisplayLabel(value),
                  ).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="space-y-3 rounded-md border border-border p-3">
            <h2 className="text-sm font-semibold">{tl('video_settings')}</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="wf-video-model" className="block text-sm font-medium">
                  {tl('model')}
                </label>
                <select
                  id="wf-video-model"
                  value={videoModel}
                  onChange={e => setVideoModel(e.target.value)}
                  disabled={viewOnly}
                  className={selectClass(viewOnly)}
                >
                  {mergeSelectOptions(
                    videoModel,
                    PROJECT_VIDEO_MODEL_VALUES,
                    value => projectVideoModelDisplayLabel(value),
                  ).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="wf-video-aspect-ratio" className="block text-sm font-medium">
                  {tl('aspect_ratio')}
                </label>
                <select
                  id="wf-video-aspect-ratio"
                  value={videoAspectRatio}
                  onChange={e => {
                    setVideoAspectRatio(e.target.value);
                    setVideoAspectRatioManuallySet(true);
                  }}
                  disabled={viewOnly}
                  className={selectClass(viewOnly)}
                >
                  {mergeSelectOptions(
                    videoAspectRatio,
                    PROJECT_VIDEO_ASPECT_RATIO_VALUES,
                    value => projectVideoAspectRatioDisplayLabel(value),
                  ).map(
                    option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ),
                  )}
                </select>
              </div>
              <div>
                <label htmlFor="wf-video-output-count" className="block text-sm font-medium">
                  {tl('output_count')}
                </label>
                <select
                  id="wf-video-output-count"
                  value={videoOutputCount}
                  onChange={e => setVideoOutputCount(Number(e.target.value))}
                  disabled={viewOnly}
                  className={selectClass(viewOnly)}
                >
                  {mergeSelectOptions(
                    videoOutputCount,
                    PROJECT_OUTPUT_COUNT_VALUES,
                    outputCountLabel,
                  ).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="wf-video-download-quality" className="block text-sm font-medium">
                  {tl('download_quality')}
                </label>
                <select
                  id="wf-video-download-quality"
                  value={videoDownloadQuality}
                  onChange={e => setVideoDownloadQuality(e.target.value)}
                  disabled={viewOnly}
                  className={selectClass(viewOnly)}
                >
                  {mergeSelectOptions(
                    videoDownloadQuality,
                    PROJECT_VIDEO_DOWNLOAD_QUALITY_VALUES,
                    value => projectVideoDownloadQualityDisplayLabel(value),
                  ).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {saveBlockedReason ? (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            role="alert"
          >
            {saveBlockedReason}
          </div>
        ) : null}

        <div className="space-y-2">
          <h2 className="text-sm font-semibold">{tl('scenes', { count: sceneCount })}</h2>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse table-fixed">
              <thead>
                <tr className="bg-muted/40 border-b border-border">
                  <th className="text-left font-medium px-3 py-2 w-16 min-w-[4rem]">
                    {col('no')}
                  </th>
                  <th className="text-left font-medium px-3 py-2 min-w-[min(100%,24rem)]">
                    {tl('text_to_image')}
                  </th>
                  <th className="text-left font-medium px-3 py-2 min-w-[min(100%,24rem)]">
                    {tl('image_to_video')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.rowId} className="border-b border-border">
                    <td className="px-3 py-2 align-top text-muted-foreground tabular-nums">
                      {index + 1}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <textarea
                        aria-label={tl('row_text_to_image_aria', { index: index + 1 })}
                        value={row.text2ImagePrompt}
                        readOnly
                        className={cn(inputClass, 'cursor-default bg-muted/30')}
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <textarea
                        aria-label={tl('row_image_to_video_aria', { index: index + 1 })}
                        value={row.image2VideoPrompt}
                        readOnly
                        className={cn(inputClass, 'cursor-default bg-muted/30')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>

        {saveErrorBelowForm ? (
          <p className="text-sm text-destructive" role="alert">
            {saveErrorBelowForm}
          </p>
        ) : null}
      </div>
    </div>
  );
}
